import { useCallback, useRef, useState } from 'react';
import {
    ArrowUpTrayIcon,
    DocumentIcon,
    XMarkIcon,
    ArrowPathIcon,
    ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import type { ManufacturerPdfRef } from '@odo/shared-ui/clinical/types';

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const ACCEPTED_MIME_TYPE = 'application/pdf';

export interface ClinicalAttachmentStepProps {
    attachment: File | null;
    attachmentRef: ManufacturerPdfRef | null;
    isUploading: boolean;
    /** Upload progress as a 0–1 fraction, or null when indeterminate. */
    uploadProgress: number | null;
    uploadError: string | null;
    onChange: (attachment: File | null) => void;
    onRetry: () => void;
    kindLabel: string;
}

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function validateFile(file: File): string | null {
    if (file.type !== ACCEPTED_MIME_TYPE) return 'Attachment must be a PDF file.';
    if (file.size > MAX_FILE_SIZE_BYTES) {
        return `Attachment exceeds the ${MAX_FILE_SIZE_BYTES / (1024 * 1024)} MB limit.`;
    }
    return null;
}

function ClinicalAttachmentStep({
    attachment,
    attachmentRef,
    isUploading,
    uploadProgress,
    uploadError,
    onChange,
    onRetry,
    kindLabel
}: ClinicalAttachmentStepProps) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [isDragActive, setIsDragActive] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);

    const acceptFile = useCallback(
        (file: File) => {
            const error = validateFile(file);
            if (error) {
                setValidationError(error);
                onChange(null);
                return;
            }
            setValidationError(null);
            onChange(file);
        },
        [onChange]
    );

    const handleDrop = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            setIsDragActive(false);
            const file = e.dataTransfer.files?.[0];
            if (file) acceptFile(file);
        },
        [acceptFile]
    );

    const handleSelect = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (file) acceptFile(file);
            e.target.value = '';
        },
        [acceptFile]
    );

    const handleRemove = useCallback(() => {
        onChange(null);
        setValidationError(null);
    }, [onChange]);

    const hasAttachment = attachment !== null || attachmentRef !== null;
    const displayName = attachment?.name ?? attachmentRef?.filename ?? '';
    const displaySize = attachment?.size ?? attachmentRef?.sizeBytes ?? 0;

    return (
        <div className="min-h-full flex items-center justify-center">
            <div className="w-full max-w-2xl flex flex-col gap-4">
                <div className="text-center">
                    <h3 className="text-sm font-semibold text-gray-800 mb-1">
                        Upload manufacturer instructions PDF
                        <span className="ml-1.5 text-xs font-normal text-gray-400">(optional)</span>
                    </h3>
                    <p className="text-xs text-gray-500">
                        If provided, this PDF will be appended after the generated {kindLabel}{' '}
                        pages. Max size 50 MB.
                    </p>
                </div>

                {hasAttachment ? (
                    <div className="flex flex-col gap-2 p-4 bg-white border border-gray-200 rounded-xl">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                <DocumentIcon className="w-5 h-5 text-gray-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-800 truncate">
                                    {displayName}
                                </div>
                                {displaySize > 0 && (
                                    <div className="text-xs text-gray-500">
                                        {formatBytes(displaySize)}
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                {isUploading && uploadProgress === null && (
                                    <span className="flex items-center gap-1 text-xs text-gray-400">
                                        <span className="w-3 h-3 rounded-full border border-gray-400 border-t-transparent animate-spin inline-block" />
                                        Uploading…
                                    </span>
                                )}
                                {isUploading && uploadProgress !== null && (
                                    <span className="text-[11px] text-gray-500">
                                        {Math.round(uploadProgress * 100)}%
                                    </span>
                                )}
                                {!isUploading && uploadError && (
                                    <button
                                        type="button"
                                        onClick={onRetry}
                                        className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800 transition-colors"
                                        title={uploadError}
                                    >
                                        <ExclamationCircleIcon className="w-4 h-4" />
                                        <ArrowPathIcon className="w-3.5 h-3.5" />
                                        Retry upload
                                    </button>
                                )}
                                {!isUploading && !uploadError && attachmentRef && (
                                    <span className="text-[10px] text-emerald-600 font-medium">
                                        ✓ Saved
                                    </span>
                                )}
                                <button
                                    type="button"
                                    onClick={handleRemove}
                                    className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                                    aria-label="Remove attachment"
                                >
                                    <XMarkIcon className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                        {isUploading && uploadProgress !== null && (
                            <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                                <div
                                    className="bg-gray-800 h-1.5 rounded-full transition-all duration-150"
                                    style={{ width: `${Math.round(uploadProgress * 100)}%` }}
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    <div
                        onDragOver={(e) => {
                            e.preventDefault();
                            setIsDragActive(true);
                        }}
                        onDragLeave={() => setIsDragActive(false)}
                        onDrop={handleDrop}
                        onClick={() => inputRef.current?.click()}
                        className={`flex flex-col items-center justify-center gap-3 p-8 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
                            isDragActive
                                ? 'border-gray-500 bg-gray-50'
                                : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50'
                        }`}
                    >
                        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                            <ArrowUpTrayIcon className="w-5 h-5 text-gray-600" />
                        </div>
                        <div className="text-center">
                            <div className="text-sm font-medium text-gray-800">
                                Drop a PDF here or click to browse
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                                PDF only · up to 50 MB
                            </div>
                        </div>
                        <input
                            ref={inputRef}
                            type="file"
                            accept={ACCEPTED_MIME_TYPE}
                            onChange={handleSelect}
                            className="hidden"
                        />
                    </div>
                )}

                {(validationError ?? uploadError) && (
                    <div className="text-xs text-red-500 text-center">
                        {validationError ?? uploadError}
                    </div>
                )}
            </div>
        </div>
    );
}

export { ClinicalAttachmentStep };
